import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuthStore } from '@/store/authStore';
import {
  NetworkMonitor,
  FirebaseInspector,
  PerformanceMetrics,
  FeatureFlags,
  SQLExplorer,
  RedisInspector,
  PubSubMonitor,
  LiveLogsViewer,
  UserImpersonation,
  ErrorsPanel,
  ApiStatus,
} from './panels';

type TabId = 'status' | 'errors' | 'sql' | 'network' | 'firebase' | 'pubsub' | 'logs' | 'redis' | 'perf' | 'flags' | 'users';

interface Tab {
  id: TabId;
  icon: string;
  label: string;
  color: string;
}

const TABS: Tab[] = [
  { id: 'status', icon: '◉', label: 'Status', color: '#22c55e' },
  { id: 'errors', icon: '⚠', label: 'Errors', color: '#ef4444' },
  { id: 'sql', icon: '⌸', label: 'SQL', color: '#3b82f6' },
  { id: 'network', icon: '◎', label: 'Network', color: '#8b5cf6' },
  { id: 'logs', icon: '≡', label: 'Logs', color: '#06b6d4' },
  { id: 'firebase', icon: '🔥', label: 'Firebase', color: '#f97316' },
  { id: 'pubsub', icon: '↗', label: 'Pub/Sub', color: '#ec4899' },
  { id: 'redis', icon: '⚡', label: 'Redis', color: '#dc2626' },
  { id: 'perf', icon: '◐', label: 'Perf', color: '#14b8a6' },
  { id: 'flags', icon: '⚑', label: 'Flags', color: '#a855f7' },
  { id: 'users', icon: '◯', label: 'Users', color: '#64748b' },
];

interface Position { x: number; y: number; }
interface Size { width: number; height: number; }

const ADMIN_EMAILS = ['duketopceo@gmail.com'];

/**
 * Debug Panel - Elegant Developer Command Center
 * A minimal, powerful debugging interface with smooth animations
 */
export const DebugPanel = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('status');
  const [position, setPosition] = useState<Position>({ x: 20, y: 80 });
  const [size, setSize] = useState<Size>({ width: 720, height: 560 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState<Position>({ x: 0, y: 0 });
  const [systemStatus, setSystemStatus] = useState<'healthy' | 'degraded' | 'down'>('healthy');
  const [errorCount] = useState(0); // TODO: Wire up error counting
  
  const panelRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user } = useAuthStore();
  
  // Access control
  const canShowDebugPanel = useMemo(() => {
    const isLocalhost = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );
    const isPreviewChannel = typeof window !== 'undefined' && (
      window.location.hostname.includes('--dev-') ||
      window.location.hostname.includes('--preview-')
    );
    const isViteDevMode = import.meta.env.DEV === true;
    const isDevEnvironment = isLocalhost || isPreviewChannel || isViteDevMode;
    const isAuthenticatedAdmin = isAuthenticated && user?.email && 
      ADMIN_EMAILS.includes(user.email.toLowerCase());
    const hasAuthenticatedDevRole = isAuthenticated && 
      (user?.role === 'admin' || user?.role === 'editor');
    
    return isDevEnvironment || isAuthenticatedAdmin || hasAuthenticatedDevRole;
  }, [isAuthenticated, user?.email, user?.role]);

  // Keyboard shortcut
  useEffect(() => {
    if (!canShowDebugPanel) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canShowDebugPanel, isOpen]);

  // Dragging
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.no-drag')) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x)),
        y: Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.y)),
      });
    }
    if (isResizing) {
      setSize({
        width: Math.max(480, Math.min(1400, e.clientX - position.x)),
        height: Math.max(360, Math.min(900, e.clientY - position.y)),
      });
    }
  }, [isDragging, isResizing, dragOffset, position, size]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Quick health check
  useEffect(() => {
    if (!isOpen) return;
    const checkHealth = async () => {
      try {
        const API_BASE = import.meta.env.VITE_API_URL || 'https://republic-atlas-api-946143380554.us-central1.run.app';
        const res = await fetch(`${API_BASE}/health`, { method: 'GET' });
        setSystemStatus(res.ok ? 'healthy' : 'degraded');
      } catch {
        setSystemStatus('down');
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, [isOpen]);

  const activeTabData = TABS.find(t => t.id === activeTab);
  
  if (!canShowDebugPanel) return null;

  return (
    <>
      {/* Floating Toggle - Minimal circular button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-[9999] group"
        title="Debug Panel (Ctrl+Shift+D)"
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: isOpen 
            ? 'linear-gradient(135deg, #1e1e2e 0%, #2d2d3d 100%)' 
            : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.1)',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isOpen ? 'rotate(45deg) scale(0.9)' : 'rotate(0) scale(1)',
        }}
      >
        <span style={{ 
          fontSize: 24, 
          color: '#fff',
          fontWeight: 300,
          transition: 'transform 0.3s ease'
        }}>
          {isOpen ? '✕' : '⚙'}
        </span>
        
        {/* Status indicator */}
        <span 
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: systemStatus === 'healthy' ? '#22c55e' : systemStatus === 'degraded' ? '#eab308' : '#ef4444',
            boxShadow: `0 0 8px ${systemStatus === 'healthy' ? '#22c55e' : systemStatus === 'degraded' ? '#eab308' : '#ef4444'}`,
            animation: systemStatus !== 'healthy' ? 'pulse 2s infinite' : 'none',
          }}
        />
        
        {/* Error badge */}
        {errorCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              left: -4,
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              background: '#ef4444',
              color: '#fff',
              fontSize: 11,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
            }}
          >
            {errorCount > 99 ? '99+' : errorCount}
          </span>
        )}
      </button>

      {/* Main Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className="fixed z-[9998] overflow-hidden"
          style={{
            left: position.x,
            top: position.y,
            width: isMinimized ? 320 : size.width,
            height: isMinimized ? 48 : size.height,
            background: 'linear-gradient(180deg, #13131a 0%, #0d0d12 100%)',
            borderRadius: 16,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05)',
            cursor: isDragging ? 'grabbing' : 'default',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            transition: isMinimized ? 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
          }}
        >
          {/* Header */}
          <div
            onMouseDown={handleMouseDown}
            style={{
              padding: '12px 16px',
              background: 'linear-gradient(90deg, rgba(99,102,241,0.15) 0%, rgba(139,92,246,0.1) 100%)',
              borderBottom: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Status dot */}
              <div style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: systemStatus === 'healthy' ? '#22c55e' : systemStatus === 'degraded' ? '#eab308' : '#ef4444',
                boxShadow: `0 0 8px ${systemStatus === 'healthy' ? '#22c55e50' : systemStatus === 'degraded' ? '#eab30850' : '#ef444450'}`,
              }} />
              
              <span style={{ color: '#fff', fontWeight: 600, fontSize: 14, letterSpacing: '-0.02em' }}>
                Debug Console
              </span>
              
              <span style={{ 
                color: 'rgba(255,255,255,0.4)', 
                fontSize: 11, 
                fontWeight: 500,
                background: 'rgba(255,255,255,0.05)',
                padding: '2px 8px',
                borderRadius: 4,
              }}>
                {import.meta.env.MODE}
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="no-drag">
              {/* Minimize */}
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: 'none',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              >
                {isMinimized ? '▢' : '—'}
              </button>
              
              {/* Close */}
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  border: 'none',
                  background: 'rgba(255,255,255,0.05)',
                  color: 'rgba(255,255,255,0.6)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239,68,68,0.2)';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                  e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                }}
              >
                ✕
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Tab Bar */}
              <div 
                style={{ 
                  display: 'flex', 
                  padding: '8px 12px',
                  gap: 4,
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                }}
                className="no-drag"
              >
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: 'none',
                      background: activeTab === tab.id ? `${tab.color}20` : 'transparent',
                      color: activeTab === tab.id ? tab.color : 'rgba(255,255,255,0.5)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 12,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      transition: 'all 0.15s ease',
                      borderBottom: activeTab === tab.id ? `2px solid ${tab.color}` : '2px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.8)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = 'rgba(255,255,255,0.5)';
                      }
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{tab.icon}</span>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div 
                style={{ 
                  flex: 1, 
                  overflowY: 'auto', 
                  padding: 16,
                  color: '#fff',
                }}
                className="no-drag"
              >
                {activeTab === 'status' && <ApiStatus />}
                {activeTab === 'errors' && <ErrorsPanel />}
                {activeTab === 'sql' && <SQLExplorer />}
                {activeTab === 'network' && <NetworkMonitor />}
                {activeTab === 'logs' && <LiveLogsViewer />}
                {activeTab === 'firebase' && <FirebaseInspector />}
                {activeTab === 'pubsub' && <PubSubMonitor />}
                {activeTab === 'redis' && <RedisInspector />}
                {activeTab === 'perf' && <PerformanceMetrics />}
                {activeTab === 'flags' && <FeatureFlags />}
                {activeTab === 'users' && <UserImpersonation />}
              </div>

              {/* Footer */}
              <div 
                style={{ 
                  padding: '8px 16px',
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.3)',
                }}
                className="no-drag"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span>{user?.email || 'Anonymous'}</span>
                  <span style={{ 
                    color: activeTabData?.color || '#8b5cf6',
                    fontWeight: 500,
                  }}>
                    {user?.role || 'guest'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{new Date().toLocaleTimeString()}</span>
                  <span style={{ opacity: 0.5 }}>Ctrl+Shift+D</span>
                </div>
              </div>

              {/* Resize Handle */}
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setIsResizing(true);
                }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  width: 16,
                  height: 16,
                  cursor: 'se-resize',
                  background: 'linear-gradient(135deg, transparent 50%, rgba(99,102,241,0.3) 50%)',
                  borderRadius: '0 0 16px 0',
                }}
                className="no-drag"
              />
            </>
          )}
        </div>
      )}

      {/* Global styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </>
  );
};

export default DebugPanel;
